/**
 * CategoryPhaseManager - Gerenciador de fases dentro do card de Categoria
 * (Tarefas | Retenção | Jurídico), no mesmo espírito do gerenciador de
 * pipelines/fases do card de Workspace do CRM.
 *
 * Permite criar, editar, excluir e reordenar (arrastar) as fases de UMA
 * categoria, sem sair da tela de Categorias. Usa somente as RPCs já
 * existentes (get_task_phases / upsert_task_phase / delete_task_phase),
 * que continuam validando tenant e permissões no servidor.
 *
 * RELEASE: 2026-10-02 v1.0.0 - Criação.
 */
import { useEffect, useState } from 'react';
import { Columns, GripVertical, Loader2, Pencil, Plus, Trash2, Flag } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQueryClient } from '@tanstack/react-query';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useTaskPhases, type TaskPhase } from '@/hooks/useTaskPhases';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { TaskPhaseFormDialog } from './TaskPhaseFormDialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: { id: string; name: string; color?: string | null } | null;
}

/** Linha arrastável de uma fase */
function SortablePhaseRow({
  phase,
  onEdit,
  onDelete,
  disabled,
}: {
  phase: TaskPhase;
  onEdit: () => void;
  onDelete: () => void;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: phase.id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-2 rounded-md border bg-card px-2 py-2 ${isDragging ? 'opacity-60 shadow-md' : ''}`}
    >
      <button
        type="button"
        className="cursor-grab text-muted-foreground disabled:cursor-not-allowed"
        aria-label="Arrastar para reordenar"
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: phase.color }} />
      <span className="text-sm font-medium truncate flex-1">{phase.name}</span>
      {phase.is_final && (
        <Badge variant="outline" className="text-[10px] gap-1"><Flag className="h-3 w-3" />Final</Badge>
      )}
      {phase.is_default && <Badge variant="outline" className="text-[10px]">Padrão</Badge>}
      <Badge variant="secondary" className="text-[10px]">{phase.tasks_count ?? 0}</Badge>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} aria-label="Editar fase">
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-destructive"
        onClick={onDelete}
        disabled={(phase.tasks_count ?? 0) > 0}
        title={(phase.tasks_count ?? 0) > 0 ? 'Fase com tarefas não pode ser excluída' : 'Excluir fase'}
        aria-label="Excluir fase"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export function CategoryPhaseManager({ open, onOpenChange, category }: Props) {
  const queryClient = useQueryClient();
  const { phases, isLoading, upsert, isUpserting, remove, reorderPhases } = useTaskPhases(category?.id);
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  // Ordem local (otimista) para o arraste
  const [ordered, setOrdered] = useState<TaskPhase[]>([]);
  const [isReordering, setIsReordering] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<TaskPhase | null>(null);

  useEffect(() => {
    setOrdered([...phases].sort((a, b) => a.sort_order - b.sort_order));
  }, [phases]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /** Atualiza contagem de fases nos cards de categoria */
  const refreshCategories = () => queryClient.invalidateQueries({ queryKey: ['task-categories'] });

  /** Persiste a nova ordem reaproveitando upsert_task_phase (sem RPC nova) */
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !category) return;
    const oldIndex = ordered.findIndex((p) => p.id === active.id);
    const newIndex = ordered.findIndex((p) => p.id === over.id);
    const next = arrayMove(ordered, oldIndex, newIndex);
    setOrdered(next);
    setIsReordering(true);
    try {
      // 2026-10-08: ordem persistida pelo hook (Tarefas ou Ordem de Serviço)
      await reorderPhases(category.id, next);
      toast.success('Ordem das fases atualizada');
    } catch (err) {
      logger.error('Erro ao reordenar fases da categoria', (err as Error).message);
      toast.error(`Erro ao reordenar fases: ${(err as Error).message}`);
    } finally {
      setIsReordering(false);
      queryClient.invalidateQueries({ queryKey: ['task-phases'] });
    }
  };

  const handleDelete = async (phase: TaskPhase) => {
    const ok = await confirmDialog({
      title: 'Excluir Fase',
      description: `Deseja realmente excluir a fase "${phase.name}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!ok) return;
    await remove(phase.id);
    refreshCategories();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden max-h-[100dvh] sm:max-h-[85vh] h-[100dvh] sm:h-auto w-full max-w-full sm:rounded-lg rounded-none flex flex-col">
          <PremiumDialogHeader
            icon={Columns}
            title={`Fases — ${category?.name ?? ''}`}
            description="Crie, edite e arraste para definir a ordem das colunas do Kanban"
          />
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
            {isLoading ? (
              [1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)
            ) : ordered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                Nenhuma fase cadastrada nesta categoria. Crie a primeira fase.
              </p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={ordered.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                  {ordered.map((p) => (
                    <SortablePhaseRow
                      key={p.id}
                      phase={p}
                      disabled={isReordering}
                      onEdit={() => { setEditing(p); setFormOpen(true); }}
                      onDelete={() => handleDelete(p)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
          <DialogFooter className="border-t bg-muted/30 px-6 py-3 flex-row justify-between sm:justify-between">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} disabled={!category || isReordering}>
              {isReordering ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Nova Fase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {category && (
        <TaskPhaseFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          categoryId={category.id}
          phase={editing}
          defaultSortOrder={ordered.length + 1}
          isSaving={isUpserting}
          onSubmit={async (input) => {
            await upsert(input);
            refreshCategories();
          }}
        />
      )}
      {ConfirmDialogComponent}
    </>
  );
}
